import './Onboarding.scss'

import { duration } from '@eturi/date-util'
import { useFn, useTimeout } from '@eturi/react'
import { useT } from '@eturi/react-i18next'
import {
	fetchUsers,
	getOnboardingSignupChoices,
	hasChildren$,
	isAuthenticated$,
	onboardingUsers$,
	setOnboardingChoices,
} from '@op/services'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { currentLang$ } from '../../../compound-selectors/language'
import { useNavTo } from '../../../hooks'
import {
	onboardingCurrentPage$,
	setOnboardingComplete,
	setOnboardingPage,
} from '../../../reducers/app-misc.slice'
import { useAppDispatch } from '../../../types'
import { Btn, Glyph, useToasts } from '../../../widgets'

export const OnboardingFooter = () => {
	const d = useAppDispatch()
	const [t] = useT()
	const navTo = useNavTo()
	const { addErrorToast } = useToasts()
	const hasChildren = useSelector(hasChildren$)
	const isAuthenticated = useSelector(isAuthenticated$)
	const locale = useSelector(currentLang$)
	const onboardingCurrentPage = useSelector(onboardingCurrentPage$)
	const onboardingUsers = useSelector(onboardingUsers$)
	const [setFallbackTimeout, clearFallbackTimeout] = useTimeout()

	const [isCompleting, setCompleting] = useState(false)

	const handleOnboardingComplete = useFn(async () => {
		if (!isAuthenticated) {
			d(setOnboardingComplete(true))
			navTo('/signup', true)
			return
		}

		const onboardingChoices = d(getOnboardingSignupChoices(locale))
		if (!onboardingChoices) return

		try {
			setCompleting(true)
			await d(setOnboardingChoices(onboardingChoices)).unwrap()
			await d(fetchUsers({ force: true })).unwrap()
		} catch {
			setCompleting(false)
			addErrorToast(t('error_strings.server_error'))
		}
	})

	useEffect(() => {
		if (!isCompleting) return

		if (hasChildren) {
			setCompleting(false)
			navTo('/manage', true)
			return
		}

		// Safety timeout so UI never hangs
		setFallbackTimeout(() => setCompleting(false), duration(12, 's'))

		return clearFallbackTimeout
	}, [hasChildren, isCompleting])

	const buttonProps = (() => {
		switch (onboardingCurrentPage) {
			case 'summary': {
				const children =
					isCompleting ? <Glyph className="refresh-spin" name="recurring" />
					: isAuthenticated ? 'Continue to Dashboard'
					: 'Sign Up to Continue'

				return {
					children,
					disabled: isCompleting,
					onClick: handleOnboardingComplete,
				}
			}

			case 'children': {
				return {
					children: 'Create Profiles',
					disabled: !onboardingUsers.length,
					onClick: () => {
						d(setOnboardingPage('loading'))
					},
				}
			}

			case 'concerns':
			default: {
				return {
					children: t('actions.continue'),
					disabled: false,
					onClick: () => {
						d(setOnboardingPage('children'))
					},
				}
			}
		}
	})()

	return (
		<div className="onboarding-shadow w-full flex justify-center bottom-0 left-0 z-10 p-6">
			<Btn className="!min-w-48" {...buttonProps} />
		</div>
	)
}
